/**
 * FlexibleWorkingHub.jsx
 * 
 * Employee portal hub for flexible working requests.
 * View existing requests and submit new ones.
 * 
 * Auth fix: uses portalApi (employee_access_token) not axiosConfig
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import axios from 'axios';

// Portal-specific axios instance — reads employee_access_token, never access_token
const portalApi = axios.create({ baseURL: '/api' });
portalApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('employee_access_token');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});
portalApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('employee_access_token');
      localStorage.removeItem('employee_refresh_token');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

const Icons = {
  ArrowLeft: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12"></line>
      <polyline points="12,19 5,12 12,5"></polyline>
    </svg>
  ),
  Plus: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"></line>
      <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  ),
  ChevronDown: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6,9 12,15 18,9"></polyline>
    </svg>
  ),
  ChevronUp: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="18,15 12,9 6,15"></polyline>
    </svg>
  ),
  Check: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20,6 9,17 4,12"></polyline>
    </svg>
  ),
  X: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  ),
  Clock: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"></circle>
      <polyline points="12,6 12,12 16,14"></polyline>
    </svg>
  ),
  PartTime: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"></circle>
      <polyline points="12,6 12,12 16,14"></polyline>
      <path d="M12 2a10 10 0 0 0 0 20" fill="currentColor" opacity="0.15"></path>
    </svg>
  ),
  Compressed: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
      <line x1="16" y1="2" x2="16" y2="6"></line>
      <line x1="8" y1="2" x2="8" y2="6"></line>
      <line x1="3" y1="10" x2="21" y2="10"></line>
      <rect x="5" y="12" width="3" height="3" rx="0.5" fill="currentColor"></rect>
      <rect x="10" y="12" width="3" height="3" rx="0.5" fill="currentColor"></rect>
      <rect x="15" y="12" width="3" height="3" rx="0.5" fill="currentColor"></rect>
      <rect x="5" y="17" width="3" height="3" rx="0.5" fill="currentColor"></rect>
    </svg>
  ),
  Flexitime: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"></circle>
      <polyline points="12,6 12,12 15,14"></polyline>
      <path d="M7 3L5 5L7 7"></path>
      <path d="M17 3L19 5L17 7"></path>
    </svg>
  ),
  JobShare: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
      <circle cx="9" cy="7" r="4"></circle>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>
  ),
  TermTime: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
      <line x1="16" y1="2" x2="16" y2="6"></line>
      <line x1="8" y1="2" x2="8" y2="6"></line>
      <line x1="3" y1="10" x2="21" y2="10"></line>
      <polyline points="9,15 11,17 15,13"></polyline>
    </svg>
  ),
  Other: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="21" x2="4" y2="14"></line>
      <line x1="4" y1="10" x2="4" y2="3"></line>
      <line x1="12" y1="21" x2="12" y2="12"></line>
      <line x1="12" y1="8" x2="12" y2="3"></line>
      <line x1="20" y1="21" x2="20" y2="16"></line>
      <line x1="20" y1="12" x2="20" y2="3"></line>
      <line x1="1" y1="14" x2="7" y2="14"></line>
      <line x1="9" y1="8" x2="15" y2="8"></line>
      <line x1="17" y1="16" x2="23" y2="16"></line>
    </svg>
  ),
  Gavel: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 3L21 9.5L9.5 21L3 14.5L14.5 3Z"></path>
      <line x1="14" y1="10" x2="10" y2="14"></line>
      <line x1="3" y1="21" x2="8" y2="16"></line>
    </svg>
  ),
};

const styles = {
  container: { minHeight: '100vh', background: '#f8fafc', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
  headerGradient: { background: 'linear-gradient(135deg, #0d9488 0%, #0891b2 50%, #0284c7 100%)', color: 'white', padding: '20px 16px', paddingTop: '24px' },
  card: { background: 'white', borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)', padding: '20px', marginBottom: '12px', transition: 'transform 0.2s ease, box-shadow 0.2s ease' },
  buttonPrimary: { background: 'linear-gradient(135deg, #0d9488 0%, #0891b2 100%)', color: 'white', border: 'none', borderRadius: '12px', padding: '14px 24px', fontWeight: '600', fontSize: '15px', cursor: 'pointer', transition: 'all 0.2s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
  buttonSecondary: { background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '12px', padding: '14px 24px', fontWeight: '600', fontSize: '15px', cursor: 'pointer', transition: 'all 0.2s ease' },
  badge: { display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
  input: { width: '100%', padding: '14px 16px', border: '2px solid #e2e8f0', borderRadius: '12px', fontSize: '15px', outline: 'none', transition: 'border-color 0.2s ease', boxSizing: 'border-box' },
  sectionHeader: { fontSize: '13px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px', marginTop: '24px' },
  iconBox: { width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
};

const FLEXIBILITY_TYPES = {
  part_time: { icon: Icons.PartTime, name: 'Part-Time Working', description: 'Reduce total working hours', color: '#0d9488' },
  compressed: { icon: Icons.Compressed, name: 'Compressed Hours', description: 'Same hours over fewer days', color: '#0891b2' },
  flexitime: { icon: Icons.Flexitime, name: 'Flexitime', description: 'Flexible start and finish times', color: '#6366f1' },
  job_share: { icon: Icons.JobShare, name: 'Job Share', description: 'Share role with another employee', color: '#8b5cf6' },
  term_time: { icon: Icons.TermTime, name: 'Term-Time Only', description: 'Work during school terms only', color: '#ec4899' },
  other: { icon: Icons.Other, name: 'Other Arrangement', description: 'Custom flexible working pattern', color: '#64748b' },
};

const getStatusStyle = (status) => {
  const s = status?.toLowerCase();
  switch (s) {
    case 'approved': case 'approved_trial': case 'appeal_upheld': return { background: '#dcfce7', color: '#166534' };
    case 'pending': case 'submitted': case 'acknowledged': return { background: '#dbeafe', color: '#1e40af' };
    case 'consultation_scheduled': case 'consultation_held': return { background: '#fef3c7', color: '#92400e' };
    case 'refused': case 'appeal_rejected': return { background: '#fee2e2', color: '#991b1b' };
    case 'appealed': return { background: '#fae8ff', color: '#86198f' };
    case 'withdrawn': return { background: '#f1f5f9', color: '#475569' };
    default: return { background: '#f1f5f9', color: '#475569' };
  }
};

const getStatusLabel = (status) => {
  const labels = { submitted: 'Submitted', acknowledged: 'Acknowledged', consultation_scheduled: 'Consultation Scheduled', consultation_held: 'Awaiting Decision', approved: 'Approved', approved_trial: 'Approved (Trial)', refused: 'Refused', appealed: 'Appealed', appeal_upheld: 'Appeal Upheld', appeal_rejected: 'Appeal Rejected', withdrawn: 'Withdrawn' };
  return labels[status?.toLowerCase()] || status;
};

const FlexibleWorkingHub = ({ onBack, employeeData }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [eligibility, setEligibility] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [appealDialog, setAppealDialog] = useState({ open: false, request: null });
  const [appealGrounds, setAppealGrounds] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ flexibility_type: '', current_pattern: '', requested_pattern: '', proposed_start_date: '', employee_reason: '', employee_impact_notes: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const requestsResponse = await portalApi.get('/flexible-working/requests');
      setRequests(requestsResponse.data.requests || []);
      const eligibilityResponse = await portalApi.get('/flexible-working/eligibility');
      setEligibility(eligibilityResponse.data);
    } catch (err) {
      console.error('Failed to load flexible working data:', err);
      setEligibility({ eligible: true, requests_remaining: 2, day_one_right: true });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRequest = async () => {
    if (!formData.flexibility_type || !formData.current_pattern || !formData.requested_pattern || !formData.proposed_start_date) {
      alert('Please fill in all required fields'); return;
    }
    setSubmitting(true);
    try {
      await portalApi.post('/flexible-working/requests', formData);
      setFormData({ flexibility_type: '', current_pattern: '', requested_pattern: '', proposed_start_date: '', employee_reason: '', employee_impact_notes: '' });
      setShowNewRequest(false);
      setActiveTab('requests');
      await loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdraw = async (requestId) => {
    if (!window.confirm('Are you sure you want to withdraw this request?')) return;
    try {
      await portalApi.post(`/flexible-working/requests/${requestId}/withdraw`);
      await loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to withdraw request');
    }
  };

  const handleAppeal = async () => {
    if (!appealGrounds.trim()) { alert('Please provide grounds for your appeal'); return; }
    setSubmitting(true);
    try {
      await portalApi.post(`/flexible-working/requests/${appealDialog.request.id}/appeal`, { appeal_grounds: appealGrounds });
      setAppealDialog({ open: false, request: null });
      setAppealGrounds('');
      await loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to submit appeal');
    } finally {
      setSubmitting(false);
    }
  };

  const renderRequestCard = (request) => {
    const typeConfig = FLEXIBILITY_TYPES[request.flexibility_type] || FLEXIBILITY_TYPES.other;
    const IconComponent = typeConfig.icon;
    const isExpanded = expandedId === request.id;
    const canWithdraw = ['submitted', 'acknowledged', 'consultation_scheduled'].includes(request.status);
    const canAppeal = request.status === 'refused';

    return (
      <div key={request.id} style={{ ...styles.card, borderLeft: `4px solid ${['approved','approved_trial'].includes(request.status) ? '#22c55e' : request.status === 'refused' ? '#ef4444' : typeConfig.color}` }}>
        <div onClick={() => setExpandedId(isExpanded ? null : request.id)} style={{ cursor: 'pointer' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ ...styles.iconBox, background: `${typeConfig.color}15`, color: typeConfig.color }}><IconComponent /></div>
              <div>
                <div style={{ fontWeight: '600', color: '#1e293b' }}>{typeConfig.name}</div>
                <div style={{ fontSize: '13px', color: '#64748b' }}>Submitted {moment(request.submitted_at).format('D MMM YYYY')}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ ...styles.badge, ...getStatusStyle(request.status) }}>{getStatusLabel(request.status)}</span>
              {isExpanded ? <Icons.ChevronUp /> : <Icons.ChevronDown />}
            </div>
          </div>
          {['submitted','acknowledged','consultation_scheduled','consultation_held'].includes(request.status) && request.response_deadline && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: request.days_until_deadline < 7 ? '#dc2626' : '#64748b' }}>
              <Icons.Clock /> Response due by {moment(request.response_deadline).format('D MMM YYYY')}
              {request.days_until_deadline !== undefined && <span style={{ fontWeight: '600' }}>({request.days_until_deadline} days)</span>}
            </div>
          )}
        </div>

        {isExpanded && (
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Current Pattern</div>
                <div style={{ fontSize: '14px', color: '#334155', background: '#f8fafc', padding: '12px', borderRadius: '8px' }}>{request.current_pattern}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Requested Pattern</div>
                <div style={{ fontSize: '14px', color: '#334155', background: '#f0fdfa', padding: '12px', borderRadius: '8px' }}>{request.requested_pattern}</div>
              </div>
              {request.employee_reason && (
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Your Reason</div>
                  <div style={{ fontSize: '14px', color: '#334155' }}>{request.employee_reason}</div>
                </div>
              )}
              <div>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Proposed Start Date</div>
                <div style={{ fontSize: '14px', color: '#334155' }}>{moment(request.proposed_start_date).format('D MMMM YYYY')}</div>
              </div>
              {['approved','approved_trial'].includes(request.status) && (
                <div style={{ background: '#dcfce7', padding: '12px', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', color: '#166534', marginBottom: '4px' }}><Icons.Check /> Request Approved</div>
                  {request.effective_date && <div style={{ fontSize: '13px', color: '#15803d' }}>Effective from {moment(request.effective_date).format('D MMMM YYYY')}</div>}
                  {request.is_trial && request.trial_end_date && <div style={{ fontSize: '13px', color: '#15803d' }}>Trial period until {moment(request.trial_end_date).format('D MMMM YYYY')}</div>}
                </div>
              )}
              {request.status === 'refused' && (
                <div style={{ background: '#fee2e2', padding: '12px', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', color: '#991b1b', marginBottom: '8px' }}><Icons.X /> Request Refused</div>
                  {request.decision_explanation && <div style={{ fontSize: '13px', color: '#b91c1c', marginBottom: '8px' }}><strong>Reason:</strong> {request.decision_explanation}</div>}
                  {request.alternative_offered && <div style={{ fontSize: '13px', color: '#b91c1c' }}><strong>Alternative offered:</strong> {request.alternative_offered}</div>}
                </div>
              )}
              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                {canWithdraw && <button onClick={(e) => { e.stopPropagation(); handleWithdraw(request.id); }} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', color: '#dc2626', cursor: 'pointer' }}>Withdraw Request</button>}
                {canAppeal && <button onClick={(e) => { e.stopPropagation(); setAppealDialog({ open: true, request }); }} style={{ ...styles.buttonPrimary, padding: '8px 16px', fontSize: '13px' }}><Icons.Gavel /> Appeal Decision</button>}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderOverview = () => (
    <>
      <div style={{ ...styles.card, background: eligibility?.eligible ? 'linear-gradient(135deg, #f0fdfa 0%, #ecfeff 100%)' : 'linear-gradient(135deg, #fef2f2 0%, #fff7ed 100%)', border: eligibility?.eligible ? '1px solid #99f6e4' : '1px solid #fecaca' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <div style={{ ...styles.iconBox, background: eligibility?.eligible ? '#0d9488' : '#ef4444', color: 'white' }}>
            {eligibility?.eligible ? <Icons.Check /> : <Icons.X />}
          </div>
          <div>
            <div style={{ fontWeight: '600', color: eligibility?.eligible ? '#134e4a' : '#991b1b' }}>{eligibility?.eligible ? 'You Can Request Flexible Working' : 'Request Limit Reached'}</div>
            <div style={{ fontSize: '13px', color: eligibility?.eligible ? '#0f766e' : '#b91c1c' }}>{eligibility?.requests_remaining || 0} request{eligibility?.requests_remaining !== 1 ? 's' : ''} remaining this year</div>
          </div>
        </div>
        <div style={{ fontSize: '13px', color: '#64748b', padding: '12px', background: 'rgba(255,255,255,0.5)', borderRadius: '8px' }}>
          <strong>Day-one right:</strong> You can request flexible working from your first day of employment. You can make up to 2 requests in any 12-month period.
        </div>
      </div>

      {eligibility?.eligible && !showNewRequest && (
        <button onClick={() => setShowNewRequest(true)} style={{ ...styles.buttonPrimary, width: '100%', marginTop: '8px' }}><Icons.Plus /> New Flexible Working Request</button>
      )}

      {showNewRequest && (
        <div style={{ ...styles.card, marginTop: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, color: '#1e293b' }}>New Request</h3>
            <button onClick={() => setShowNewRequest(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}><Icons.X /></button>
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '12px' }}>What type of flexibility do you need? *</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
              {Object.entries(FLEXIBILITY_TYPES).map(([key, config]) => {
                const IconComponent = config.icon;
                return (
                  <div key={key} onClick={() => setFormData({ ...formData, flexibility_type: key })} style={{ padding: '12px', borderRadius: '12px', border: formData.flexibility_type === key ? `2px solid ${config.color}` : '2px solid #e2e8f0', background: formData.flexibility_type === key ? `${config.color}10` : 'white', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ ...styles.iconBox, width: '36px', height: '36px', background: formData.flexibility_type === key ? config.color : '#f1f5f9', color: formData.flexibility_type === key ? 'white' : '#64748b' }}><IconComponent /></div>
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '13px', color: '#1e293b' }}>{config.name}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{config.description}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Your current working pattern *</label>
            <textarea value={formData.current_pattern} onChange={(e) => setFormData({ ...formData, current_pattern: e.target.value })} placeholder="e.g., Monday to Friday, 9am-5pm, based at the salon" rows={3} style={{ ...styles.input, resize: 'vertical', minHeight: '80px' }} />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Your proposed working pattern *</label>
            <textarea value={formData.requested_pattern} onChange={(e) => setFormData({ ...formData, requested_pattern: e.target.value })} placeholder="Describe exactly what you're requesting..." rows={3} style={{ ...styles.input, resize: 'vertical', minHeight: '80px' }} />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>When would you like this to start? *</label>
            <input type="date" value={formData.proposed_start_date} onChange={(e) => setFormData({ ...formData, proposed_start_date: e.target.value })} min={moment().add(1, 'month').format('YYYY-MM-DD')} style={styles.input} />
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Allow time for the consultation process</div>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Why are you making this request? (Optional)</label>
            <textarea value={formData.employee_reason} onChange={(e) => setFormData({ ...formData, employee_reason: e.target.value })} placeholder="You don't have to give a reason, but it may help your case..." rows={2} style={{ ...styles.input, resize: 'vertical', minHeight: '60px' }} />
          </div>
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>How could this work for the business? (Optional)</label>
            <textarea value={formData.employee_impact_notes} onChange={(e) => setFormData({ ...formData, employee_impact_notes: e.target.value })} placeholder="Your thoughts on how the business could accommodate this..." rows={2} style={{ ...styles.input, resize: 'vertical', minHeight: '60px' }} />
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={() => setShowNewRequest(false)} style={{ ...styles.buttonSecondary, flex: 1 }}>Cancel</button>
            <button onClick={handleSubmitRequest} disabled={submitting || !formData.flexibility_type || !formData.current_pattern || !formData.requested_pattern || !formData.proposed_start_date} style={{ ...styles.buttonPrimary, flex: 1, opacity: submitting || !formData.flexibility_type || !formData.current_pattern || !formData.requested_pattern || !formData.proposed_start_date ? 0.5 : 1 }}>
              {submitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </div>
      )}

      {requests.filter(r => ['submitted','acknowledged','consultation_scheduled','consultation_held'].includes(r.status)).length > 0 && (
        <>
          <div style={styles.sectionHeader}>Pending Requests</div>
          {requests.filter(r => ['submitted','acknowledged','consultation_scheduled','consultation_held'].includes(r.status)).map(req => renderRequestCard(req))}
        </>
      )}

      <div style={styles.sectionHeader}>Your Rights</div>
      <div style={styles.card}>
        <div style={{ fontSize: '14px', color: '#334155', lineHeight: '1.6' }}>
          {[['Day-one right', 'You can request flexible working from your first day'], ['2-month deadline', 'Your employer must respond within 2 months'], ['Consultation required', 'Your employer must discuss the request with you'], ['Right to appeal', 'You can appeal if your request is refused']].map(([title, text]) => (
            <div key={title} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '12px' }}>
              <div style={{ color: '#0d9488', marginTop: '2px' }}><Icons.Check /></div>
              <span><strong>{title}</strong> – {text}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.headerGradient}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button onClick={onBack} style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}><Icons.ArrowLeft /></button>
            <h1 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>Flexible Working</h1>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
          <div style={{ width: '32px', height: '32px', border: '3px solid #e2e8f0', borderTopColor: '#0d9488', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.headerGradient}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={onBack} style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}><Icons.ArrowLeft /></button>
          <div>
            <h1 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>Flexible Working</h1>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>Request changes to your working pattern</div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', background: 'white', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 10 }}>
        {['overview', 'requests'].map((tab) => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{ flex: 1, padding: '16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: activeTab === tab ? '#0d9488' : '#64748b', borderBottom: activeTab === tab ? '2px solid #0d9488' : '2px solid transparent', transition: 'all 0.2s ease' }}>
            {tab === 'overview' && 'Overview'}{tab === 'requests' && `My Requests (${requests.length})`}
          </button>
        ))}
      </div>

      <div style={{ padding: '16px' }}>
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'requests' && (
          requests.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 16px', color: '#64748b' }}>
              <div style={{ width: '64px', height: '64px', margin: '0 auto 16px', background: '#f1f5f9', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}><Icons.Clock /></div>
              <div style={{ fontWeight: '600', marginBottom: '8px' }}>No Requests Yet</div>
              <div style={{ fontSize: '14px' }}>Your flexible working requests will appear here</div>
            </div>
          ) : (
            requests.sort((a, b) => moment(b.submitted_at).diff(moment(a.submitted_at))).map(req => renderRequestCard(req))
          )
        )}
      </div>

      {appealDialog.open && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '20px', padding: '24px', maxWidth: '500px', width: '100%', maxHeight: '80vh', overflow: 'auto' }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#1e293b' }}>Appeal Decision</h3>
            <div style={{ background: '#eff6ff', padding: '12px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px', color: '#1e40af' }}>
              You can appeal if you believe the decision was based on incorrect facts, didn't properly consider your request, or used an invalid reason.
            </div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Grounds for Appeal *</label>
            <textarea value={appealGrounds} onChange={(e) => setAppealGrounds(e.target.value)} placeholder="Explain why you are appealing this decision..." rows={4} style={{ ...styles.input, resize: 'vertical', minHeight: '100px', marginBottom: '16px' }} />
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => { setAppealDialog({ open: false, request: null }); setAppealGrounds(''); }} style={{ ...styles.buttonSecondary, flex: 1 }}>Cancel</button>
              <button onClick={handleAppeal} disabled={submitting || !appealGrounds.trim()} style={{ ...styles.buttonPrimary, flex: 1, opacity: submitting || !appealGrounds.trim() ? 0.5 : 1 }}>{submitting ? 'Submitting...' : 'Submit Appeal'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlexibleWorkingHub;